import { FaCheckCircle, FaClipboardList } from "react-icons/fa";
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";

function AuditedCountCard({ auditedTarget, auditedCompleted, loading }) {
  const [displayedCompleted, setDisplayedCompleted] = useState(0);
  const [displayedPercentage, setDisplayedPercentage] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Function to animate numbers dynamically
  const animateNumbers = () => {
    let startCompleted = 0;
    let startPercentage = 0;
    const endCompleted = auditedCompleted || 0;
    const endPercentage = auditedTarget
      ? Math.round((auditedCompleted / auditedTarget) * 100)
      : 0;

    const duration = 1500; // 1.5 seconds
    const incrementCompleted = Math.ceil(endCompleted / 50);
    const incrementPercentage = Math.ceil(endPercentage / 50);

    const timer = setInterval(() => {
      startCompleted += incrementCompleted;
      startPercentage += incrementPercentage;

      if (startCompleted >= endCompleted) {
        clearInterval(timer);
        setDisplayedCompleted(endCompleted);
        setDisplayedPercentage(endPercentage);
        setIsComplete(true); // Mark as complete to stop animations
      } else {
        setDisplayedCompleted(startCompleted);
        setDisplayedPercentage(startPercentage);
      }
    }, duration / 50);
  };

  useEffect(() => {
    animateNumbers();
  }, [auditedCompleted]);

  // Dynamic color based on completion percentage
  const progressColor = useMemo(() => {
    if (displayedPercentage < 40) return "bg-red-500"; // Low Progress - Red
    if (displayedPercentage < 70) return "bg-yellow-500"; // Moderate Progress - Orange
    return "bg-green-500"; // High Progress - Green
  }, [displayedPercentage]);

  const textColor = useMemo(() => {
    if (displayedPercentage < 40) return "text-red-600"; // Low
    if (displayedPercentage < 70) return "text-yellow-600"; // Moderate
    return "text-green-600"; // High
  }, [displayedPercentage]);

  // Restart animation when hovering
  const handleMouseEnter = () => {
    setDisplayedCompleted(0);
    setDisplayedPercentage(0);
    setIsComplete(false);
    animateNumbers();
  };

  return (
    <motion.div 
      className="bg-white p-6  flex flex-col transition-transform duration-300  w-full"
      whileHover={{ scale: 1.05 }}
      onMouseEnter={handleMouseEnter} // Restart animation when hovering
    >
      
      {/* Header Section */}
      <div className="flex items-center">
        <div className="p-4 rounded-full bg-gray-100 shadow-md">
          <FaClipboardList className="text-gray-700" size={40} />
        </div>
        <h5 className="ml-4 text-xl font-bold text-gray-800">Audited Count</h5>
      </div>

      {/* Loading State */}
      {loading ? (
        <p className="text-gray-500 text-lg mt-4">Loading...</p>
      ) : (
        <>
          {/* Target vs Completed */}
          <div className="grid grid-cols-2 gap-6 mt-6 items-center">
            
            {/* Left Side: Completed Count with Bouncing Effect */}
            <motion.div 
              className="text-left"
              key={displayedCompleted}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, bounce: 0.3 }}
            >
              <p className="text-lg font-semibold text-gray-600">Completed</p>
              <motion.p 
                className={`mt-2 text-4xl font-bold ${textColor}`}
                animate={isComplete ? {} : { scale: [1, 1.2, 1] }}
                transition={{ duration: 0.8, repeat: isComplete ? 0 : Infinity, repeatType: "reverse" }}
              >
                {displayedCompleted}
              </motion.p>
            </motion.div>

            {/* Right Side: Target with Badge */}
            <div className="relative flex items-center justify-center bg-gray-200 rounded-xl p-4 shadow-md">
              <FaCheckCircle className="text-green-500 text-3xl mr-2" />
              <div>
                <p className="text-sm font-semibold text-gray-700">Target</p>
                <p className="text-xl font-bold text-gray-900">{auditedTarget}</p>
              </div>
            </div>
          </div>

          {/* Progress Bar with Dynamic Colors */}
          <motion.div 
            className="relative w-full h-5 bg-gray-00 rounded-lg overflow-hidden mt-6 shadow-inner"
          >
            <motion.div
              className={`absolute top-0 left-0 h-full ${progressColor} rounded-lg`}
              initial={{ width: "0%" }}
              animate={{ width: `${displayedPercentage}%` }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            />

            {/* Wave Animation Stops When Complete */}
            {!isComplete && (
              <motion.div 
                className="absolute w-full h-full opacity-30 bg-white"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              />
            )}
          </motion.div>

          {/* Percentage Achieved with Glow Effect */}
          <motion.p 
            className={`text-center text-lg font-bold ${textColor} mt-3`}
            key={displayedPercentage}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ textShadow: isComplete ? "none" : "0px 0px 8px rgba(0,0,0,0.2)" }}
          >
            {displayedPercentage}% Achieved
          </motion.p>
        </>
      )}
    </motion.div>
  );
}

export default AuditedCountCard;
